import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar, Check, ChevronsUpDown } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";
import CustomDatePicker from "@/components/CustomDatePicker";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { PropertyContract, useCreateContract, useUpdateContract } from "@/hooks/useContracts";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";

interface ContractFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract?: PropertyContract | null;
}

interface FormData {
  organization_id: string;
  property_id: string;
  operation_start_date: string;
  cleaning_rate: string;
  administration_rate: string;
  administration_commission: string;
  promotional_commission: string;
  promotion_end_date: string;
  status: 'active' | 'cancelled';
  admin_type: 'Propio' | 'Renta' | 'Administración';
  notes: string;
  rental_payment_day: string;
  rental_monthly_amount: string;
  rental_iva_percentage: string;
}

const ContractFormModal = ({ isOpen, onClose, contract }: ContractFormModalProps) => {
  const { data: properties } = useProperties();
  const { data: organizations } = useOrganizations();
  const createMutation = useCreateContract();
  const updateMutation = useUpdateContract();
  
  const [formData, setFormData] = useState<FormData>({
    organization_id: "",
    property_id: "",
    operation_start_date: "",
    cleaning_rate: "",
    administration_rate: "",
    administration_commission: "",
    promotional_commission: "",
    promotion_end_date: "",
    status: "active",
    admin_type: "Propio",
    notes: "",
    rental_payment_day: "",
    rental_monthly_amount: "",
    rental_iva_percentage: "16",
  });

  const [isPropertyOpen, setIsPropertyOpen] = useState(false);
  const [isOrganizationOpen, setIsOrganizationOpen] = useState(false);

  useEffect(() => {
    if (contract) {
      setFormData({
        organization_id: contract.organization_id || "",
        property_id: contract.property_id,
        operation_start_date: contract.operation_start_date,
        cleaning_rate: contract.cleaning_rate.toString(),
        administration_rate: contract.administration_rate.toString(),
        administration_commission: contract.administration_commission.toString(),
        promotional_commission: contract.promotional_commission?.toString() || "",
        promotion_end_date: contract.promotion_end_date || "",
        status: contract.status,
        admin_type: contract.admin_type,
        notes: contract.notes || "",
        rental_payment_day: contract.rental_payment_day?.toString() || "",
        rental_monthly_amount: contract.rental_monthly_amount?.toString() || "",
        rental_iva_percentage: contract.rental_iva_percentage?.toString() || "16",
      });
    } else {
      setFormData({
        organization_id: "",
        property_id: "",
        operation_start_date: "",
        cleaning_rate: "",
        administration_rate: "",
        administration_commission: "",
        promotional_commission: "",
        promotion_end_date: "",
        status: "active",
        admin_type: "Propio",
        notes: "",
        rental_payment_day: "",
        rental_monthly_amount: "",
        rental_iva_percentage: "16",
      });
    }
  }, [contract, isOpen]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-calcular IVA al cambiar admin_type a Renta o al ingresar monto
      if (field === "admin_type" && value === "Renta" && !prev.rental_iva_percentage) {
        newData.rental_iva_percentage = "16";
      }
      
      // Limpiar campos de renta si se cambia a otro tipo
      if (field === "admin_type" && value !== "Renta") {
        newData.rental_payment_day = "";
        newData.rental_monthly_amount = "";
        newData.rental_iva_percentage = "16";
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const contractData = {
      organization_id: formData.organization_id || undefined,
      property_id: formData.property_id,
      operation_start_date: formData.operation_start_date,
      cleaning_rate: parseFloat(formData.cleaning_rate),
      administration_rate: parseFloat(formData.administration_rate),
      administration_commission: parseFloat(formData.administration_commission),
      promotional_commission: formData.promotional_commission ? parseFloat(formData.promotional_commission) : undefined,
      promotion_end_date: formData.promotion_end_date || undefined,
      status: formData.status,
      admin_type: formData.admin_type,
      notes: formData.notes || undefined,
      rental_payment_day: formData.rental_payment_day ? parseInt(formData.rental_payment_day) : undefined,
      rental_monthly_amount: formData.rental_monthly_amount ? parseFloat(formData.rental_monthly_amount) : undefined,
      rental_iva_percentage: formData.rental_iva_percentage ? parseFloat(formData.rental_iva_percentage) : undefined,
    };

    try {
      if (contract) {
        await updateMutation.mutateAsync({ id: contract.id, ...contractData });
      } else {
        await createMutation.mutateAsync(contractData);
      }
      onClose();
    } catch (error) {
      console.error("Error al guardar contrato:", error);
    }
  };

  const getPropertyName = (propertyId: string) => {
    const property = properties?.find(p => p.id === propertyId);
    return property?.shortName || property?.title || propertyId;
  };

  const getOrganizationName = (organizationId: string) => {
    const organization = organizations?.find(o => o.id === organizationId);
    return organization?.name || organizationId;
  };

  const availableProperties = properties?.filter(property => {
    if (contract && property.id === contract.property_id) {
      return true;
    }
    // Solo mostrar propiedades que no tienen un contrato activo
    return true; // La restricción se maneja en la base de datos
  }) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {contract ? "Editar Contrato" : "Nuevo Contrato"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="organization">Organización</Label>
            <Popover open={isOrganizationOpen} onOpenChange={setIsOrganizationOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isOrganizationOpen}
                  className="w-full justify-between"
                >
                  {formData.organization_id 
                    ? getOrganizationName(formData.organization_id)
                    : "Seleccionar organización..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Buscar organización..." />
                  <CommandList>
                    <CommandEmpty>No se encontraron organizaciones.</CommandEmpty>
                    <CommandGroup>
                      {organizations?.map((organization) => (
                        <CommandItem
                          key={organization.id}
                          value={organization.name}
                          onSelect={() => {
                            handleInputChange("organization_id", organization.id);
                            setIsOrganizationOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              formData.organization_id === organization.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {organization.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label htmlFor="property">Propiedad *</Label>
            <Popover open={isPropertyOpen} onOpenChange={setIsPropertyOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isPropertyOpen}
                  className="w-full justify-between"
                >
                  {formData.property_id 
                    ? getPropertyName(formData.property_id)
                    : "Seleccionar propiedad..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Buscar propiedad..." />
                  <CommandList>
                    <CommandEmpty>No se encontraron propiedades.</CommandEmpty>
                    <CommandGroup>
                      {availableProperties.map((property) => (
                        <CommandItem
                          key={property.id}
                          value={property.shortName || property.title}
                          onSelect={() => {
                            handleInputChange("property_id", property.id);
                            setIsPropertyOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              formData.property_id === property.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {property.shortName || property.title}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label htmlFor="operation_start_date">Inicio de Operación *</Label>
            <CustomDatePicker
              selected={formData.operation_start_date ? new Date(formData.operation_start_date + 'T12:00:00') : undefined}
              onSelect={(date) => {
                if (date) {
                  // Asegurar que la fecha se guarde en formato local sin cambios de zona horaria
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  handleInputChange("operation_start_date", `${year}-${month}-${day}`);
                }
              }}
              placeholder="Seleccionar fecha"
              className="w-full h-fit"
              isAdminContext
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cleaning_rate">Tarifa de Limpieza (MXN) *</Label>
              <Input
                id="cleaning_rate"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.cleaning_rate}
                onChange={(e) => handleInputChange("cleaning_rate", e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="administration_rate">Tarifa de Administración (MXN) *</Label>
              <Input
                id="administration_rate"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.administration_rate}
                onChange={(e) => handleInputChange("administration_rate", e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="administration_commission">Comisión de Administración (%) *</Label>
              <Input
                id="administration_commission"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="0.00"
                value={formData.administration_commission}
                onChange={(e) => handleInputChange("administration_commission", e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="promotional_commission">Comisión Promocional (%)</Label>
              <Input
                id="promotional_commission"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="0.00"
                value={formData.promotional_commission}
                onChange={(e) => handleInputChange("promotional_commission", e.target.value)}
              />
            </div>
          </div>

          {formData.promotional_commission && (
            <div>
              <Label htmlFor="promotion_end_date">Vigencia de Promoción</Label>
              <CustomDatePicker
                selected={formData.promotion_end_date ? new Date(formData.promotion_end_date + 'T12:00:00') : undefined}
                onSelect={(date) => {
                  if (date) {
                    // Asegurar que la fecha se guarde en formato local sin cambios de zona horaria
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    handleInputChange("promotion_end_date", `${year}-${month}-${day}`);
                  }
                }}
                placeholder="Seleccionar fecha de fin"
                className="w-full h-fit"
                isAdminContext
              />
            </div>
          )}

          <div>
            <Label htmlFor="status">Estado *</Label>
            <Select
              value={formData.status}
              onValueChange={(value: 'active' | 'cancelled') =>
                handleInputChange("status", value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="admin_type">Tipo de Administración *</Label>
            <Select
              value={formData.admin_type}
              onValueChange={(value: 'Propio' | 'Renta' | 'Administración') =>
                handleInputChange("admin_type", value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Propio">Propio</SelectItem>
                <SelectItem value="Renta">Renta</SelectItem>
                <SelectItem value="Administración">Administración</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.admin_type === 'Renta' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="rental_payment_day">Día de Pago *</Label>
                  <Select
                    value={formData.rental_payment_day}
                    onValueChange={(value) => handleInputChange("rental_payment_day", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Día del mes" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          Día {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="rental_monthly_amount">Monto Mensual (MXN) *</Label>
                  <Input
                    id="rental_monthly_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.rental_monthly_amount}
                    onChange={(e) => handleInputChange("rental_monthly_amount", e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="rental_iva_percentage">IVA (%)</Label>
                  <Input
                    id="rental_iva_percentage"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="16"
                    value={formData.rental_iva_percentage}
                    onChange={(e) => handleInputChange("rental_iva_percentage", e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              placeholder="Notas adicionales sobre el contrato..."
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending 
                ? "Guardando..." 
                : contract ? "Actualizar" : "Crear"
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContractFormModal;